import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { DeleteIcon } from 'foundations-assets';
import { useViewerActions } from '../../hooks/useViewerActions';
import { useCallback, useContext, useMemo } from 'react';
import ConditionBoxDeletePopover from '../common/ConditionBoxDeletePopover';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { getExactKeyPath, isLogicGroup } from '../../filterQueryFormat/logic/LogicGroup';
import { isOperator } from '../../filterQueryFormat/operator/Operator';
import { useStrategy } from '../../hooks/useStrategy';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled from 'foundations-theming/utils';
import { countMatchingConditions } from '../../utils/countMatchingConditions';
import { jsx as _jsx } from "react/jsx-runtime";
export const HoverDeleteButtonContainer = styled.div.withConfig({
  displayName: "ViewerConditionClickableBoxDeleteButton__HoverDeleteButtonContainer",
  componentId: "sc-14u99e4-0"
})(({
  showDeleteButton,
  theme
}) => ({
  display: showDeleteButton ? 'block' : 'none',
  position: 'absolute',
  listStyle: 'none',
  top: 2,
  right: 2,
  borderRadius: theme.borderRadius[100]
}));
const ViewerConditionClickableBoxDeleteButton = ({
  conditionPath,
  isFilterBranchDeleteTriggered = false,
  showHoverDeleteButton = false,
  use = 'default'
}) => {
  const {
    internalDraftValue
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getRequiredFields,
    isRequiredConditionForEvent
  } = useStrategy();
  const {
    onConditionDeleteHover,
    onConditionDeleteTrigger,
    onConditionDeleteCancel,
    onConditionDeleteConfirm
  } = useViewerActions();
  const conditionToDelete = useMemo(() => {
    const condition = getExactKeyPath(conditionPath, internalDraftValue);
    let conditionFilterFamily;
    if (condition && isOperator(condition)) {
      const parentLogicGroup = getExactKeyPath(conditionPath.pop(), internalDraftValue);
      conditionFilterFamily = parentLogicGroup.filterFamily;
    } else if (condition && isLogicGroup(condition)) {
      conditionFilterFamily = condition.filterFamily;
    }
    return {
      condition,
      filterFamily: conditionFilterFamily
    };
  }, [internalDraftValue, conditionPath]);
  const disabledDeleteButtonTooltip = useMemo(() => {
    if (conditionToDelete && conditionToDelete.filterFamily && isOperator(conditionToDelete.condition)) {
      const requiredFields = getRequiredFields(conditionToDelete.filterFamily);
      const isRequiredCondition = conditionToDelete.condition.field && requiredFields.has(conditionToDelete.condition.field.name) || isRequiredConditionForEvent(conditionToDelete.filterFamily, conditionToDelete.condition);
      if (isRequiredCondition) {
        const parentPath = conditionPath.pop();
        const parentGroup = getExactKeyPath(parentPath, internalDraftValue);
        const matchingConditions = countMatchingConditions(parentGroup, requiredFields, conditionToDelete.filterFamily, isRequiredConditionForEvent);
        if (matchingConditions > 1) {
          return undefined;
        }
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.requiredFieldDisabledButtonTooltip"
        });
      }
    }
    return undefined;
  }, [conditionToDelete, getRequiredFields, isRequiredConditionForEvent, conditionPath, internalDraftValue]);
  const onDelete = useCallback(event => {
    event.stopPropagation();
    onConditionDeleteTrigger(conditionPath);
  }, [onConditionDeleteTrigger, conditionPath]);
  return /*#__PURE__*/_jsx(ConditionBoxDeletePopover, {
    isOpen: isFilterBranchDeleteTriggered,
    onCancel: onConditionDeleteCancel,
    onConfirm: () => onConditionDeleteConfirm(conditionPath),
    children: use === 'hover' ? /*#__PURE__*/_jsx(HoverDeleteButtonContainer, {
      showDeleteButton: showHoverDeleteButton,
      children: /*#__PURE__*/_jsx(Tooltip, {
        disabled: !disabledDeleteButtonTooltip,
        title: disabledDeleteButtonTooltip,
        children: /*#__PURE__*/_jsx(IconButton, {
          disabled: Boolean(disabledDeleteButtonTooltip),
          "data-test-id": "delete-button",
          onClick: onDelete,
          onMouseEnter: () => onConditionDeleteHover(conditionPath),
          onMouseLeave: () => onConditionDeleteHover(undefined),
          size: "xs",
          children: /*#__PURE__*/_jsx(DeleteIcon, {})
        })
      })
    }) : /*#__PURE__*/_jsx(Tooltip, {
      disabled: !disabledDeleteButtonTooltip,
      title: disabledDeleteButtonTooltip,
      children: /*#__PURE__*/_jsx(IconButton, {
        disabled: Boolean(disabledDeleteButtonTooltip),
        "data-test-id": "delete-button",
        onClick: onDelete,
        onMouseEnter: () => onConditionDeleteHover(conditionPath),
        onMouseLeave: () => onConditionDeleteHover(undefined),
        size: "md",
        use: "transparent",
        padding: "0",
        children: /*#__PURE__*/_jsx(DeleteIcon, {})
      })
    })
  });
};
export default ViewerConditionClickableBoxDeleteButton;